import {
  orderVolumeTier,
  qualifiesForSalesNudge,
  smartPricingAnalyticsTier,
} from "@src/constants/billing";

import type { BillingModule, BillingPlan } from "./types";
import type {
  BillingAccountState,
  BillingActiveEntityViewModel,
  BillingAdjustmentViewModel,
  BillingCadence,
  BillingChipViewModel,
  BillingCouponViewModel,
  BillingEntityCalloutViewModel,
  BillingEntityViewModel,
  BillingManagementViewModel,
  BillingOptionViewModel,
  BillingPageViewModel,
  BillingPlanChangeDialogViewModel,
  BillingPriceViewModel,
  BillingSavingsLineViewModel,
  BillingTrialEndingDisclosureViewModel,
  SmartPricingVariantViewModel,
} from "../types";
import {
  EXPERT_SUPPORT_CALLOUT,
  SMART_CONTENT_ID,
  SMART_PRICING_LIMITED_ID,
  SMART_PRICING_UNLIMITED_ID,
  SMART_PRICING_VARIANTS,
  STATIC_MARKETING_ANNUAL_DISCOUNT_PERCENTAGE,
  UNLIMITED_ID,
} from "./constants";
import {
  formatDate,
  formatMonthDay,
  formatShortDate,
} from "@intelligems/ig-utils/date-time";
import { getRemainingDaysUntil } from "@intelligems/ig-utils/stripe";
import {
  commitmentDroppedModules,
  isBridgeOnlyTrial,
  isPlanCommitmentSelection,
  selectionEndsTrial,
  trialSubmitNeedsShopify,
} from "./trialCommitment";

import {
  billingSubscriptionFor,
  buildBillingCatalog,
  collapseSmartPricingVariants,
  isPausedAccount,
  merchantFacingStatus,
  presentedActiveModules,
} from "./catalog";
import {
  billingMonthsSince,
  type BillingPeriod,
  commitmentHasEnded,
} from "./boundary";
import { hasExpertSupport, isExistingCustomer } from "./gateProjection";
import { isTerminalSubscriptionStatus } from "./lapsedFreeTrial";
import {
  applyCouponsToAmount,
  getBundleDiscountPct,
  getCommittedSelection,
  getEffectiveSavingsPct,
  getEligibleModuleCount,
  getEntityCadencePrice,
  getNextTierBundleDiscountPct,
  getSelectionTotals,
  getUnlimitedSavings,
  getUnlimitedUpgradeSavings,
  quoteSelection,
  roundToCents,
  scaleFlatCouponsToCadence,
} from "./pricing";
import { formatModuleList } from "./formatting";
import { projectProgress } from "./progressProjection";
import { isSelectionNoOp } from "./selection";
import { projectTrialOutcome } from "./trialOutcome";
import type {
  BillingCatalog,
  BillingCycle,
  BillingSnapshot,
  Selection,
} from "./types";

type CatalogEntity = BillingModule | BillingPlan;
type SelectionState = Selection & { reactivatingEntityId: string | null };

const toPublicCadence = (cadence: BillingCycle): BillingCadence =>
  cadence === "year" ? "annual" : "monthly";

const suffixFor = (cadence: BillingCycle): "/mo" | "/yr" =>
  cadence === "year" ? "/yr" : "/mo";

const makePrice = ({
  amount,
  cadence,
  compareAt,
  savingsPct,
}: {
  amount: number;
  cadence: BillingCycle;
  compareAt?: number;
  savingsPct?: number;
}): BillingPriceViewModel => ({
  amount,
  ...(compareAt !== undefined ? { compareAt } : {}),
  suffix: suffixFor(cadence),
  ...(savingsPct !== undefined && savingsPct > 0 ? { savingsPct } : {}),
});

const catalogAmount = (
  entity: CatalogEntity,
  cadence: BillingCycle
): number | undefined => {
  const amount =
    cadence === "year" ? entity.intervalPrice : entity.currentPrice;
  return amount !== undefined && amount > 0 ? amount : undefined;
};

const projectEntityPrice = (
  entity: CatalogEntity,
  cadence: BillingCycle,
  savingsPct = 0
): BillingPriceViewModel | null => {
  const compareAt = catalogAmount(entity, cadence);
  if (compareAt === undefined) {
    return null;
  }

  if (savingsPct <= 0) {
    const previousPrice =
      entity.previousPrice !== undefined && entity.previousPrice > compareAt
        ? entity.previousPrice
        : undefined;
    return makePrice({
      amount: compareAt,
      cadence,
      compareAt: previousPrice,
    });
  }

  return makePrice({
    amount: roundToCents(compareAt * (1 - savingsPct / 100)),
    cadence,
    compareAt,
    savingsPct,
  });
};

const projectActiveEntityPrice = (
  entity: CatalogEntity,
  rateEntity: CatalogEntity | undefined,
  cadence: BillingCycle,
  savingsPct: number
): BillingPriceViewModel | null => {
  if (cadence === "month") {
    return projectEntityPrice(entity, cadence, savingsPct);
  }
  if (entity.currentPrice <= 0) {
    return null;
  }

  const hasActualPriceOverride =
    rateEntity !== undefined && entity.currentPrice !== rateEntity.currentPrice;
  const compareAt = hasActualPriceOverride
    ? entity.currentPrice * 12
    : (entity.intervalPrice ?? entity.currentPrice * 12);
  return makePrice({
    amount: roundToCents(compareAt * (1 - savingsPct / 100)),
    cadence,
    ...(savingsPct > 0 ? { compareAt, savingsPct } : {}),
  });
};

type CommitmentPresentation = {
  /**
   * True when THIS row's minimum term is the current plan's own, not a term the module
   * carries separately. The plan's row already states it ("Active: 3 month minimum"), so
   * repeating it per module reads as a second, separate commitment.
   *
   * Asserted of the row rather than of the plan, because a plan with a term of its own
   * does not thereby cover every module sitting next to it: the backend advertises an
   * `add_module` path for every module even to an Unlimited org, so a separately billed
   * module on a committed plan is a shape it permits, and that module's minimum is its
   * own to state. See the call site for the signal that tells the two apart.
   *
   * Presentation only. What the badge reads is `minimumCommitmentMonths`; the matching
   * chip is suppressed alongside it to keep the view model self-consistent, though no
   * active-module row renders chips today. The catalog entity keeps its `commitment`,
   * which is what `entityActions` reads, so the row's cancel/pause timing and its
   * commitment tooltip are unchanged.
   */
  planCoversCommitment?: boolean;
};

const projectChips = (
  entity: CatalogEntity,
  savingsPct?: number,
  { planCoversCommitment = false }: CommitmentPresentation = {}
): readonly BillingChipViewModel[] => {
  const chips: BillingChipViewModel[] = (entity.chips ?? [])
    .filter(({ slot }) => slot !== "savings")
    .map(({ icon, label, slot, variant }) => ({
      ...(icon ? { icon } : {}),
      label,
      ...(slot ? { slot } : {}),
      tone: variant,
    }));

  if (savingsPct !== undefined && savingsPct > 0) {
    chips.unshift({
      label: `Save ${savingsPct}%`,
      slot: "savings",
      tone: "success",
    });
  }

  if (
    !planCoversCommitment &&
    entity.type === "module" &&
    entity.commitment === 3
  ) {
    chips.push({
      label: "3 month minimum",
      tone: "info",
    });
  }

  return chips;
};

const projectEntity = (
  entity: CatalogEntity,
  price: BillingPriceViewModel | null,
  commitment: CommitmentPresentation = {}
): BillingEntityViewModel => ({
  analyticsTier: smartPricingAnalyticsTier(entity.id),
  chips: projectChips(entity, price?.savingsPct, commitment),
  description: entity.description,
  entityType: entity.type,
  features: [...entity.features],
  ...(entity.icon ? { icon: entity.icon } : {}),
  id: entity.id,
  isMandatory: entity.id === SMART_CONTENT_ID,
  isSmartPricing: SMART_PRICING_VARIANTS.has(entity.id),
  isUnlimited: entity.id === UNLIMITED_ID,
  minimumCommitmentMonths: commitment.planCoversCommitment
    ? 0
    : entity.commitment,
  name: entity.name,
  price,
});

type EntityProjectionContext = CommitmentPresentation & {
  allowsCoreModuleCancel?: boolean;
  hasCommittedPlan?: boolean;
  isBridgeTrial?: boolean;
  isBundled?: boolean;
  isEnded?: boolean;
};

const activeEntityState = (
  entity: CatalogEntity,
  { isEnded }: EntityProjectionContext
): BillingActiveEntityViewModel["state"] => {
  if (isEnded) {
    return "ended";
  }
  if (entity.cancelledAt) {
    return "scheduled_cancel";
  }
  if (entity.type === "module" && entity.scheduledRemoval) {
    return "scheduled_removal";
  }
  if (entity.pauseAt) {
    return "scheduled_pause";
  }
  if (entity.type === "plan" && entity.status === "past_due") {
    return "past_due";
  }
  if (entity.type === "plan" && entity.status === "paused") {
    return "paused";
  }
  return "active";
};

export const PAST_DUE_TOOLTIP =
  "Click 'Manage' in the top right to pay off your balance.";

const entityStatus = (
  entity: CatalogEntity,
  state: BillingActiveEntityViewModel["state"]
): BillingActiveEntityViewModel["status"] => {
  switch (state) {
    case "scheduled_cancel":
      return {
        label: `Cancels on ${entity.cancelledAt ?? ""}`.trim(),
        tone: "default",
      };
    case "scheduled_removal":
      return {
        label: `Cancels on ${
          entity.type === "module" && entity.scheduledRemoval
            ? formatShortDate(entity.scheduledRemoval.effectiveAt)
            : ""
        }`.trim(),
        tone: "default",
      };
    case "scheduled_pause":
      return {
        label: `Scheduled to pause ${entity.pauseAt ?? ""}`.trim(),
        tone: "warning",
      };
    case "ended":
      return entity.type === "plan"
        ? { label: "Trial Ended", tone: "error" }
        : {
            label: entity.trialEndAt
              ? `Ended ${formatMonthDay(entity.trialEndAt)}`
              : "Ended",
            tone: "default",
          };
    case "past_due":
      return {
        label: "Past due",
        tone: "warning",
        tooltip: PAST_DUE_TOOLTIP,
      };
    case "paused":
      return { label: "Paused", tone: "default" };
    case "active":
      if ("status" in entity && entity.status === "trialing") {
        return { label: "Trialing", tone: "success" };
      }
      return { label: "Active", tone: "success" };
  }
};

const isInsideCommitment = (
  entity: CatalogEntity,
  period: BillingPeriod | undefined,
  nowSec: number
): boolean =>
  period !== undefined && !commitmentHasEnded(entity, period, nowSec);

const commitmentTooltip = (entity: CatalogEntity): string => {
  const startedOn = entity.startDate
    ? `Started on ${new Date(entity.startDate * 1_000).toLocaleDateString(
        "en-US",
        { day: "numeric", month: "long", year: "numeric" }
      )}. `
    : "";
  return `You're in a ${entity.commitment}-month minimum commitment. ${startedOn}We'll schedule for the earliest date after your ${entity.commitment}-month minimum ends. You keep full access until then and can undo anytime.`;
};

// A pending pause is undone through the command that parked it; while the
// command id is still unknown (an optimistic pause) there is nothing to offer.
const pauseActions = (
  entity: CatalogEntity
): BillingActiveEntityViewModel["actions"] => {
  if (!entity.pauseAt) {
    return [
      {
        intent: { type: "pause", entityId: entity.id },
        label: "Pause plan",
        tone: "default",
      },
    ];
  }
  if (!entity.pendingPauseCommandId) {
    return [];
  }

  return [
    {
      intent: {
        type: "cancel-pending-pause",
        entityId: entity.id,
        sourceCommandId: entity.pendingPauseCommandId,
      },
      label: "Cancel pause",
      tone: "default",
    },
  ];
};

const coreModuleCancelTooltip = (name: string): string =>
  `${name} powers core features other modules rely on. Canceling it cancels your whole plan.`;

const entityActions = (
  entity: CatalogEntity,
  nowSec: number,
  period: BillingPeriod | undefined,
  {
    allowsCoreModuleCancel,
    hasCommittedPlan,
    isBridgeTrial,
    isBundled,
    isEnded,
  }: EntityProjectionContext
): BillingActiveEntityViewModel["actions"] => {
  if (isEnded) {
    return [];
  }

  if (entity.cancelledAt) {
    return [
      isBridgeTrial
        ? {
            intent: { type: "choose-plan", entityId: entity.id },
            label: hasCommittedPlan ? "Change plan" : "Choose plan",
            tone: "default",
          }
        : {
            intent: { type: "release", entityId: entity.id },
            label: "Reactivate",
            tone: "default",
          },
    ];
  }

  if (entity.type === "module" && entity.scheduledRemoval) {
    return [
      {
        intent: {
          type: "cancel-pending-removal",
          entityId: entity.id,
          sourceCommandId: entity.scheduledRemoval.commandId,
        },
        label: "Reactivate",
        tone: "default",
      },
    ];
  }

  const scheduled = isInsideCommitment(entity, period, nowSec);
  if (entity.type === "plan") {
    if (entity.id !== UNLIMITED_ID) {
      return [];
    }
    const timing: "scheduled" | "immediate" = scheduled
      ? "scheduled"
      : "immediate";
    return [
      ...pauseActions(entity),
      {
        intent: { type: "cancel", entityId: entity.id, timing },
        label: scheduled ? "Schedule cancel" : `Cancel ${entity.name}`,
        tone: "danger",
        ...(scheduled ? { tooltip: commitmentTooltip(entity) } : {}),
      },
    ];
  }

  if (
    entity.type === "module" &&
    entity.id === SMART_CONTENT_ID &&
    allowsCoreModuleCancel
  ) {
    return [
      {
        intent: { type: "cancel-core-module", entityId: entity.id },
        label: "Cancel",
        tone: "danger",
        tooltip: coreModuleCancelTooltip(entity.name),
      },
    ];
  }

  // A confirmed trial is a module the merchant has committed to, so it cancels
  // like any other active module. Its commitment only starts when the trial
  // converts, so it never routes through the commitment branches below.
  if (entity.type === "module" && entity.trialUnconfirmable) {
    return [
      {
        intent: { type: "remove-module", entityId: entity.id },
        label: `Cancel ${entity.name}`,
        tone: "danger",
      },
    ];
  }

  // A module trialEndAt (ADR 0013, distinct from the account-level trial handled above) must be checked before the commitment===0 branch below, or canceling would be misrouted to a generic remove.
  if (entity.trialEndAt !== undefined) {
    return [
      {
        intent: { type: "cancel-trial", entityId: entity.id },
        label: "End trial now",
        tone: "danger",
      },
    ];
  }

  if (entity.commitment === 0) {
    return [
      {
        intent: { type: "remove-module", entityId: entity.id },
        label: `Cancel ${entity.name}`,
        tone: "danger",
      },
    ];
  }

  const timing: "scheduled" | "immediate" = scheduled
    ? "scheduled"
    : "immediate";
  return [
    {
      intent: { type: "cancel", entityId: entity.id, timing },
      label: scheduled ? "Schedule cancel" : `Cancel ${entity.name}`,
      tone: "danger",
      ...(scheduled ? { tooltip: commitmentTooltip(entity) } : {}),
      // A bundled module has no subscription of its own, so its cancel command
      // cannot find one and is refused on submit; and cancelling the plan's
      // subscription instead would end the whole plan, not this module. There is
      // no operation behind this item until removing a module from a bundle
      // exists, so it stays visible but cannot be used.
      ...(isBundled ? { disabled: true } : {}),
    },
    // A bundled module has no subscription of its own to pause; the plan's row
    // pauses the subscription that bills it.
    ...(isBundled ? [] : pauseActions(entity)),
  ];
};

const projectActiveEntity = (
  entity: CatalogEntity,
  price: BillingPriceViewModel | null,
  nowSec: number,
  period: BillingPeriod | undefined,
  context: EntityProjectionContext = {}
): BillingActiveEntityViewModel => {
  const state = activeEntityState(entity, context);

  return {
    ...projectEntity(entity, price, context),
    actions: entityActions(entity, nowSec, period, context),
    ...(context.isEnded ? { chips: [] } : {}),
    monthsActive:
      entity.startDate && period
        ? billingMonthsSince(entity.startDate, period, nowSec)
        : 0,
    ...(entity.cancelledAt ? { cancelledAt: entity.cancelledAt } : {}),
    ...(entity.pauseAt ? { pauseAt: entity.pauseAt } : {}),
    ...(entity.type === "plan" && entity.billingFrequency
      ? { billingFrequency: entity.billingFrequency }
      : {}),
    // Emitted whenever a removal is parked, independent of `state`: a
    // subscription-wide cancel makes `activeEntityState` report
    // `scheduled_cancel` and mask the removal, but the boundary is still true.
    ...(entity.type === "module" && entity.scheduledRemoval
      ? { removalEffectiveAt: entity.scheduledRemoval.effectiveAt }
      : {}),
    ...(entity.type === "plan" && entity.renewalDate
      ? { renewalDate: entity.renewalDate }
      : {}),
    ...(entity.startDate !== undefined ? { startedAt: entity.startDate } : {}),
    state,
    status: entityStatus(entity, state),
    ...(entity.trialEndAt !== undefined
      ? { trialEndAt: entity.trialEndAt }
      : {}),
    ...(entity.type === "module" && entity.trialConverts
      ? { trialConverts: true }
      : {}),
  };
};

/**
 * Dates each trialing module the merchant is not keeping past the trial. A
 * module's own scheduled removal outranks a commitment's start, since it is the
 * boundary that removal will actually run on.
 */
const withTrialDepartures = (
  activeModules: readonly BillingActiveEntityViewModel[],
  dropped: ReturnType<typeof commitmentDroppedModules>
): BillingActiveEntityViewModel[] =>
  activeModules.map((module) => {
    const endsAfterTrialAt =
      module.removalEffectiveAt ??
      (dropped && !dropped.keeps.has(module.id) ? dropped.at : undefined);

    return endsAfterTrialAt === undefined
      ? module
      : { ...module, endsAfterTrialAt };
  });

export const projectBillingAccountState = (
  snapshot: BillingSnapshot
): BillingAccountState => {
  const { active, historical } = snapshot.subscriptionAccessData.subscriptions;
  // Each row read exactly as the catalog reads it, so the account state and the
  // chip cannot disagree about whether a Shopify bridge is dunning. The rule
  // itself — which settling statuses a bridge may hold, and why a missing
  // `billingProvider` is not one — lives on `merchantFacingStatus` alone.
  const effectiveStatuses = active.map((subscription) =>
    merchantFacingStatus(subscription)
  );

  if (effectiveStatuses.includes("trialing")) {
    return "trialing";
  }
  // Ahead of the status checks below, because a paused org's subscription still
  // reports `active` to Stripe — it carries only the pause retention price — so the
  // owned pause product is the only signal, and reading status first would report a
  // paused merchant as simply active.
  //
  // The order is a deliberate trade-off: a paused org whose retention invoice fails
  // reports `paused` and its `past_due` is masked. Pause is a state the merchant
  // chose and can see, whereas that dunning combination is rare, so pause wins.
  if (isPausedAccount(snapshot)) {
    return "paused";
  }
  // A settling Shopify bridge already reads `active` above, which is what keeps
  // a merchant who has paid off the arms below: those match on trial history, so
  // falling past this one offers a free trial to someone already paying.
  if (effectiveStatuses.includes("active")) {
    return "active";
  }
  if (effectiveStatuses.includes("past_due")) {
    return "past_due";
  }

  // Broader than the Trial Ended card on purpose (#5647): any org whose subscription
  // has ended stops being shown "start your free trial", churned payers included. The
  // card is stricter — `findLapsedFreeTrial` in `catalog.ts` also requires that the
  // subscription stopped no later than its trial end — because only a trial that
  // never converted can be offered back as a trial.
  //
  // Terminal is required so a `pending` subscription awaiting external approval, or a
  // paused one — alive, just not currently live, and so also sorted into
  // `historical` — is never told its trial expired.
  const trialEnded = historical.some(
    ({ status, trialEnd }) => trialEnd && isTerminalSubscriptionStatus(status)
  );

  return trialEnded ? "expired" : "pre_trial";
};

// The full ordered Operator Coupon stack on the primary active subscription.
// This is the operator stack specifically — NOT the flattened `coupon`, which
// conflates the bundle discount that is already reflected in the catalog-
// derived total. Drives both the displayed price reduction and the per-coupon
// discount pills. See ADR-0009.
const primaryActiveSubscription = (snapshot: BillingSnapshot) => {
  const { active } = snapshot.subscriptionAccessData.subscriptions;
  return (
    active.find(({ status, cancelAt }) => status === "active" && !cancelAt) ??
    active[0]
  );
};

/**
 * When a trialing org's plan takes effect: its committed plan's start, or,
 * before it commits, the trial end, since that is when any plan would start.
 */
const trialPlanStartsOn = (snapshot: BillingSnapshot): string | null => {
  const { planCommitment, subscriptions } = snapshot.subscriptionAccessData;
  const startsAt =
    planCommitment?.startsAt ??
    subscriptions.active.find(({ status }) => status === "trialing")?.trialEnd;

  return startsAt != null ? formatDate(startsAt) : null;
};

const trialEndingDisclosure = ({
  amount,
  catalog,
  selection,
  snapshot,
}: {
  amount: number;
  catalog: BillingCatalog;
  selection: SelectionState;
  snapshot: BillingSnapshot;
}): BillingTrialEndingDisclosureViewModel | null => {
  const trialEnd = snapshot.subscriptionAccessData.subscriptions.active.find(
    ({ status }) => status === "trialing"
  )?.trialEnd;
  if (trialEnd == null || !selectionEndsTrial(selection)) {
    return null;
  }

  const entities = [
    ...catalog.plans.filter(({ id }) => id === selection.planId),
    ...catalog.modules.filter(({ id }) => selection.moduleIds.includes(id)),
  ];
  const endingEntity = entities.find(
    ({ id }) => id === UNLIMITED_ID || SMART_PRICING_VARIANTS.has(id)
  );
  return {
    amount,
    cadence: selection.cadence,
    daysLeft: Math.max(0, getRemainingDaysUntil(trialEnd)),
    endingEntityName: endingEntity?.name ?? "",
    moduleNames: entities.map(({ name }) => name),
  };
};

const operatorCouponsFor = (
  snapshot: BillingSnapshot
): readonly BillingCouponViewModel[] =>
  primaryActiveSubscription(snapshot)?.operatorCoupons ?? [];

const selectedIdsFor = (selection: Selection): string[] =>
  selection.planId
    ? [selection.planId, ...selection.moduleIds]
    : [...selection.moduleIds];

// The à-la-carte comparison basket holds every module a customer could own at once.
// Summing both Smart Pricing variants would double-count it in the savings story, so
// the basket carries the one tier a merchant can actually hold.
const completeModuleBasket = (catalog: BillingCatalog): BillingModule[] =>
  collapseSmartPricingVariants(catalog.modules);

const completeModuleAmount = (
  catalog: BillingCatalog,
  cadence: BillingCycle
): number =>
  completeModuleBasket(catalog).reduce(
    (sum, module) => sum + (catalogAmount(module, cadence) ?? 0),
    0
  );

const unlimitedSavingsFor = (
  catalog: BillingCatalog,
  unlimited: BillingPlan
): { savings: number; pct: number } | null => {
  const unlimitedAmount = catalogAmount(unlimited, catalog.cadence);
  if (
    unlimitedAmount === undefined ||
    completeModuleBasket(catalog).some(
      (module) => catalogAmount(module, catalog.cadence) === undefined
    )
  ) {
    return null;
  }

  return getUnlimitedSavings(
    completeModuleAmount(catalog, catalog.cadence),
    unlimitedAmount
  );
};

const unlimitedOfferFor = (
  snapshot: BillingSnapshot,
  catalog: BillingCatalog
): BillingPageViewModel["unlimitedOffer"] => {
  if (catalog.currentPlan?.id === UNLIMITED_ID) {
    return null;
  }

  const { active } = snapshot.subscriptionAccessData.subscriptions;
  const subscription =
    active.find(({ status, cancelAt }) => status === "active" && !cancelAt) ??
    active[0];
  if (subscription?.status !== "cancelled") {
    return null;
  }

  const { blocked, deferred, eligible } =
    snapshot.subscriptionAccessData.upgradeDowngradePaths;
  const eligiblePath = [...eligible, ...deferred].find(
    ({ changeType }) => changeType === "upgrade_to_blue"
  );
  const blockedPath = blocked.find(
    ({ changeType, decision }) =>
      changeType === "upgrade_to_blue" &&
      decision.reason === "billing_command_in_progress"
  );
  if (!eligiblePath && !blockedPath) {
    return null;
  }

  const unlimited = catalog.plans.find(
    ({ id, isCurrent }) => id === UNLIMITED_ID && !isCurrent
  );
  const amount = unlimited && catalogAmount(unlimited, catalog.cadence);
  if (!unlimited || amount === undefined) {
    return null;
  }

  const unlimitedSavings = unlimitedSavingsFor(catalog, unlimited);
  if (!unlimitedSavings) {
    return null;
  }
  const { pct, savings } = unlimitedSavings;
  return {
    entity: projectEntity(
      unlimited,
      makePrice({
        amount,
        cadence: catalog.cadence,
        compareAt: completeModuleAmount(catalog, catalog.cadence),
        savingsPct: pct,
      })
    ),
    savings: makePrice({
      amount: savings,
      cadence: catalog.cadence,
      savingsPct: pct,
    }),
  };
};

// The Unlimited card advertises the white-glove support package only to
// merchants big enough to be assigned a CSM. Everyone else sees the same card
// without the callout.
const expertSupportCalloutFor = (
  ordersPerMonth: number
): BillingEntityCalloutViewModel | undefined =>
  hasExpertSupport(orderVolumeTier(ordersPerMonth))
    ? EXPERT_SUPPORT_CALLOUT
    : undefined;

export const projectBillingPage = ({
  snapshot,
  catalog,
  management,
  nowSec = Date.now() / 1_000,
  stagedCoupon,
  ordersPerMonth,
}: {
  snapshot: BillingSnapshot;
  catalog: BillingCatalog;
  management: BillingManagementViewModel;
  nowSec?: number;
  stagedCoupon?: Readonly<{
    id: string;
    name?: string;
    percentOff?: number;
    amountOff?: number;
  }> | null;
  ordersPerMonth: number;
}): BillingPageViewModel => {
  const selection: Selection = {
    cadence: catalog.cadence,
    moduleIds: catalog.activeModules.map(({ id }) => id),
    planId: catalog.currentPlan?.id === UNLIMITED_ID ? UNLIMITED_ID : undefined,
  };
  const totals = getSelectionTotals(
    { plans: catalog.plans, modules: catalog.modules },
    selectedIdsFor(selection),
    catalog.cadence
  );
  const isUnlimited = selection.planId === UNLIMITED_ID;
  const currentUnlimitedSavings =
    isUnlimited && catalog.currentPlan
      ? unlimitedSavingsFor(catalog, catalog.currentPlan)
      : null;

  const hasCurrentPrices = isUnlimited
    ? (catalog.currentPlan?.currentPrice ?? 0) > 0
    : selection.moduleIds.every((id) =>
        catalog.modules.some(
          (module) => module.id === id && module.currentPrice > 0
        )
      );

  // Excludes actively-trialing modules from the headline price/discount (Stripe's bundle coupon excludes them too) — `totals` below stays unfiltered for the compareAt strikethrough.
  const billableTotals = getSelectionTotals(
    { plans: catalog.plans, modules: catalog.modules },
    selectedIdsFor({
      ...selection,
      moduleIds: catalog.activeModules
        .filter(({ trialEndAt }) => trialEndAt === undefined)
        .map(({ id }) => id),
    }),
    catalog.cadence
  );

  const currentAmount =
    catalog.cadence === "year"
      ? billableTotals.intervalTotal
      : billableTotals.monthlyTotal;

  const currentCompareAt = isUnlimited
    ? currentUnlimitedSavings
      ? completeModuleAmount(catalog, catalog.cadence)
      : currentAmount
    : totals.selectionSum;

  const currentTotal =
    totals.selectedEntities.length > 0 && hasCurrentPrices
      ? makePrice({
          amount: currentAmount,
          cadence: catalog.cadence,
          compareAt:
            currentCompareAt > currentAmount ? currentCompareAt : undefined,
          savingsPct:
            billableTotals.bundleDiscountPct > 0
              ? billableTotals.bundleDiscountPct
              : undefined,
        })
      : null;

  // Reduce the displayed plan total by the operator coupon stack (the bundle
  // discount is already baked into `currentTotal`). Amount only: `compareAt`
  // (à-la-carte) and `savingsPct` (bundle) stay put, so the strikethrough
  // keeps its existing story and the coupon pills explain the drop. The
  // pre-coupon `currentTotal` is retained for the upsell math below (a promo
  // must not shrink it). The staged coupon is appended, not substituted: it
  // is about to join the live stack, not replace it. `currentTotal` is a
  // per-cadence equivalent of the live subscription's period, so flat coupons
  // are scaled to that cadence before they come off.
  const operatorCoupons = [
    ...operatorCouponsFor(snapshot),
    ...(stagedCoupon ? [stagedCoupon] : []),
  ];
  const displayTotal =
    currentTotal && operatorCoupons.length > 0
      ? {
          ...currentTotal,
          amount: applyCouponsToAmount(
            currentTotal.amount,
            scaleFlatCouponsToCadence(
              operatorCoupons,
              primaryActiveSubscription(snapshot),
              catalog.cadence
            )
          ),
        }
      : currentTotal;

  const eligibleModuleCount = getEligibleModuleCount(catalog.activeModules);
  const nextSavingsPct = getNextTierBundleDiscountPct(eligibleModuleCount);
  const upsell =
    currentTotal && eligibleModuleCount < 4
      ? {
          currentModuleCount: eligibleModuleCount,
          nextSavingsPct,
          potentialSavings: makePrice({
            amount: Math.round(
              ((currentTotal.compareAt ?? currentTotal.amount) *
                (nextSavingsPct - totals.bundleDiscountPct)) /
                100
            ),
            cadence: catalog.cadence,
          }),
          title: `Add 1 more module to unlock ${nextSavingsPct}% off your whole plan`,
        }
      : null;

  const rateCatalog = rateCatalogFor(snapshot, catalog.cadence);

  const allowsCoreModuleCancel =
    catalog.currentPlan !== undefined &&
    catalog.currentPlan.id !== UNLIMITED_ID &&
    catalog.currentPlan.cancelledAt === undefined &&
    catalog.currentPlan.status !== "paused" &&
    catalog.currentPlan.status !== "trialing";

  const isBridgeTrial = isBridgeOnlyTrial(snapshot);
  const hasCommittedPlan =
    snapshot.subscriptionAccessData.planCommitment != null;
  const activeModules = presentedActiveModules(catalog).map((module) => {
    // A bundled module has no subscription of its own — that is why it has no
    // `startDate` — so the only minimum term it can be inside, and the only pause that
    // can stop it, are the plan's. A module the merchant bought separately keeps its own
    // subscription, and with it the minimum it signed up to and its own pause state.
    const isBundled = isUnlimited && module.startDate === undefined;
    // When the subscription backing the current plan is scheduled to cancel,
    // modules that carry no cancel schedule of their own inherit that
    // subscription-wide cancellation. Their price is projected away so the row
    // shows the cancel notice in place of a price, matching the current plan.
    const inheritsSubscriptionCancel =
      module.cancelledAt === undefined &&
      catalog.currentPlan?.cancelledAt !== undefined;
    const effectiveModule = {
      ...module,
      cancelledAt: module.cancelledAt ?? catalog.currentPlan?.cancelledAt,
      ...(isBundled
        ? {
            pauseAt: catalog.currentPlan?.pauseAt,
            pendingPauseCommandId: catalog.currentPlan?.pendingPauseCommandId,
          }
        : {}),
    };
    return projectActiveEntity(
      effectiveModule,
      inheritsSubscriptionCancel
        ? null
        : projectActiveEntityPrice(
            module,
            rateCatalog.modules.find(({ id }) => id === module.id),
            catalog.cadence,
            billableTotals.bundleDiscountPct
          ),
      nowSec,
      billingSubscriptionFor(snapshot, catalog, module.id),
      {
        allowsCoreModuleCancel,
        hasCommittedPlan,
        isBridgeTrial,
        isBundled,
        planCoversCommitment: isBundled,
      }
    );
  });

  const currentPlan = catalog.currentPlan
    ? projectActiveEntity(
        catalog.currentPlan,
        displayTotal,
        nowSec,
        billingSubscriptionFor(snapshot, catalog, catalog.currentPlan.id),
        { hasCommittedPlan, isBridgeTrial }
      )
    : null;

  const endedTrialTotals = getSelectionTotals(
    { plans: catalog.plans, modules: catalog.modules },
    catalog.endedTrialModules.map(({ id }) => id),
    catalog.cadence
  );
  const endedTrialAmount =
    catalog.cadence === "year"
      ? endedTrialTotals.intervalTotal
      : endedTrialTotals.monthlyTotal;
  const endedTrialPrice =
    endedTrialTotals.selectedEntities.length > 0 && endedTrialAmount > 0
      ? makePrice({
          amount: endedTrialAmount,
          cadence: catalog.cadence,
          compareAt:
            endedTrialTotals.selectionSum > endedTrialAmount
              ? endedTrialTotals.selectionSum
              : undefined,
          savingsPct:
            endedTrialTotals.bundleDiscountPct > 0
              ? endedTrialTotals.bundleDiscountPct
              : undefined,
        })
      : null;
  const endedTrial = catalog.endedTrialPlan
    ? {
        plan: projectActiveEntity(
          catalog.endedTrialPlan,
          endedTrialPrice,
          nowSec,
          billingSubscriptionFor(snapshot, catalog, catalog.endedTrialPlan.id),
          { isEnded: true }
        ),
        modules: catalog.endedTrialModules.map((module) =>
          projectActiveEntity(
            module,
            null,
            nowSec,
            billingSubscriptionFor(snapshot, catalog, module.id),
            { isEnded: true }
          )
        ),
      }
    : null;

  const cardModuleIds = new Set([
    ...catalog.activeModules.map(({ id }) => id),
    ...catalog.endedTrialModules.map(({ id }) => id),
  ]);

  const discoveryOptions: BillingOptionViewModel[] = catalog.modules
    .filter(
      ({ id }) => !cardModuleIds.has(id) && id !== SMART_PRICING_UNLIMITED_ID
    )
    .map((module) => {
      const futureBundleDiscountPct = getBundleDiscountPct({
        modules: [...catalog.activeModules, module],
      });
      const price = projectEntityPrice(
        module,
        catalog.cadence,
        futureBundleDiscountPct
      );
      return {
        ...projectEntity(module, price),
        ...(price === null ? { disabledReason: "Price unavailable" } : {}),
        isDisabled: price === null,
        isSelected: false,
        isTrialing: false,
      };
    });

  const unlimited = catalog.plans.find(
    ({ id, isCurrent }) => id === UNLIMITED_ID && !isCurrent
  );

  if (unlimited) {
    const amount = catalogAmount(unlimited, catalog.cadence);
    const discoveryModules = catalog.modules.filter(
      ({ id }) => id !== SMART_PRICING_UNLIMITED_ID
    );
    const activeDiscoveryModules = catalog.activeModules.filter(
      ({ id }) => id !== SMART_PRICING_UNLIMITED_ID
    );
    const allDiscoveryPricesAvailable = discoveryModules.every(
      (module) => catalogAmount(module, catalog.cadence) !== undefined
    );
    const fullModulesTotal = discoveryModules.reduce(
      (sum, module) => sum + (catalogAmount(module, catalog.cadence) ?? 0),
      0
    );
    const activeModulesTotal = activeDiscoveryModules.reduce(
      (sum, module) => sum + (catalogAmount(module, catalog.cadence) ?? 0),
      0
    );
    const savingsPct =
      amount !== undefined && allDiscoveryPricesAvailable
        ? getUnlimitedUpgradeSavings({
            activeModulesTotal,
            fullModulesTotal,
            unlimitedPrice: amount,
          }).pct
        : 0;
    const price =
      amount === undefined
        ? null
        : makePrice({
            amount,
            cadence: catalog.cadence,
            savingsPct,
          });

    const callout = expertSupportCalloutFor(ordersPerMonth);

    discoveryOptions.push({
      ...projectEntity(unlimited, price),
      ...(callout ? { callout } : {}),
      ...(price === null ? { disabledReason: "Price unavailable" } : {}),
      isDisabled: price === null,
      isSelected: false,
      isTrialing: false,
    });
  }

  const hasCancelledPlan =
    !!catalog.currentPlan?.cancelledAt ||
    snapshot.subscriptionAccessData.subscriptions.active.some(
      ({ status }) => status === "cancelled"
    );

  const accountState = projectBillingAccountState(snapshot);
  // Only a live trial has a conversion story to tell, and only while the plan itself is
  // staying: a subscription-wide cancellation takes every module with it, which the
  // cancel chip and its undo already say. Shares `hasCancelledPlan` rather than
  // restating half of it, so "this plan is cancelled" cannot come to mean two things
  // on one page.
  const isTrialConverting = accountState === "trialing" && !hasCancelledPlan;
  const trialOutcome = isTrialConverting
    ? projectTrialOutcome(activeModules)
    : null;

  return {
    accountState,
    activeModules: isTrialConverting
      ? withTrialDepartures(activeModules, commitmentDroppedModules(snapshot))
      : activeModules,
    coupons: operatorCoupons,
    currentPlan,
    currentTotal: displayTotal,
    endedTrial,
    isExistingCustomer: isExistingCustomer(
      snapshot.subscriptionAccessData.subscriptions
    ),
    discovery: {
      options: discoveryOptions,
      progress: projectProgress({
        bundleSavingsPct: totals.bundleDiscountPct,
        isUnlimited,
        modules: catalog.activeModules,
        unlimitedSavingsPct: currentUnlimitedSavings?.pct,
      }),
      state: hasCancelledPlan
        ? "cancelled_plan"
        : currentPlan || activeModules.length > 0
          ? "active_plan"
          : "no_plan",
      upsell,
    },
    management,
    showSalesNudge:
      accountState !== "pre_trial" &&
      !isUnlimited &&
      qualifiesForSalesNudge(ordersPerMonth),
    trialOutcome,
    unlimitedOffer: unlimitedOfferFor(snapshot, catalog),
  };
};

const rateCatalogFor = (
  snapshot: BillingSnapshot,
  cadence: BillingCycle
): BillingCatalog =>
  buildBillingCatalog(
    { ...snapshot, actualPriceByProductId: new Map() },
    cadence
  );

const hasPrice = (
  entity: CatalogEntity | undefined,
  cadence: BillingCycle
): entity is CatalogEntity =>
  entity !== undefined && catalogAmount(entity, cadence) !== undefined;

const optionDisabledReason = ({
  isActive,
  isMandatory,
  isTrialing,
  price,
}: {
  isActive: boolean;
  isMandatory: boolean;
  isTrialing: boolean;
  price: BillingPriceViewModel | null;
}): string | undefined => {
  if (price === null) {
    return "Price unavailable";
  }
  if (isMandatory) {
    return "Included with every selection";
  }
  // A trialing module is on the subscription but not paid for, so selecting it is what commits to it.
  return isActive && !isTrialing ? "Already active" : undefined;
};

const projectDialogOptions = ({
  bundleSavingsPct,
  catalog,
  rateCatalog,
  selection,
  trialingPriceIds,
}: {
  bundleSavingsPct: number;
  catalog: BillingCatalog;
  rateCatalog: BillingCatalog;
  selection: SelectionState;
  trialingPriceIds: ReadonlySet<string>;
}): BillingOptionViewModel[] => {
  const activeIds = new Set([
    ...catalog.activeModules.map(({ id }) => id),
    ...(catalog.currentPlan?.id === UNLIMITED_ID ? [UNLIMITED_ID] : []),
  ]);

  const smartPricingBase =
    catalog.modules.find(({ id }) => id === SMART_PRICING_LIMITED_ID) ??
    catalog.modules.find(({ id }) => id === SMART_PRICING_UNLIMITED_ID);
  const modules = catalog.modules.filter(
    ({ id }) => !SMART_PRICING_VARIANTS.has(id)
  );
  const selectedRateModules = rateCatalog.modules.filter(({ id }) =>
    selection.moduleIds.includes(id)
  );

  if (smartPricingBase) {
    const firstVariantIndex = catalog.modules.findIndex(({ id }) =>
      SMART_PRICING_VARIANTS.has(id)
    );
    modules.splice(Math.max(0, firstVariantIndex), 0, smartPricingBase);
  }

  return [...catalog.plans, ...modules].map((entity) => {
    const isSmartPricing = SMART_PRICING_VARIANTS.has(entity.id);
    const isSelected =
      entity.type === "plan"
        ? selection.planId === entity.id
        : isSmartPricing
          ? selection.moduleIds.some((id) => SMART_PRICING_VARIANTS.has(id))
          : selection.moduleIds.includes(entity.id);

    const selectedSmartPricingId = selection.moduleIds.find((id) =>
      SMART_PRICING_VARIANTS.has(id)
    );

    const pricedEntity =
      isSmartPricing && selectedSmartPricingId
        ? catalog.modules.find(({ id }) => id === selectedSmartPricingId)
        : entity;

    const suppliedHasPrice = hasPrice(pricedEntity, catalog.cadence);

    const rateEntity =
      entity.type === "plan"
        ? rateCatalog.plans.find(({ id }) => id === entity.id)
        : rateCatalog.modules.find(
            ({ id }) => id === (pricedEntity?.id ?? entity.id)
          );

    const optionBundleSavingsPct =
      rateEntity && entity.type === "module" && !selection.planId
        ? isSelected
          ? bundleSavingsPct
          : getBundleDiscountPct({
              modules: [...selectedRateModules, rateEntity],
            })
        : 0;

    let price =
      suppliedHasPrice && rateEntity
        ? projectEntityPrice(
            rateEntity,
            catalog.cadence,
            optionBundleSavingsPct
          )
        : null;

    const isMandatory = entity.id === SMART_CONTENT_ID;
    const isActive = isSmartPricing
      ? catalog.activeModules.some(({ id }) => SMART_PRICING_VARIANTS.has(id))
      : activeIds.has(entity.id);

    if (entity.id === UNLIMITED_ID && price) {
      const rateUnlimited = rateCatalog.plans.find(
        ({ id }) => id === UNLIMITED_ID
      );

      if (rateUnlimited) {
        const unlimitedSavings = unlimitedSavingsFor(
          rateCatalog,
          rateUnlimited
        );
        price = makePrice({
          amount: price.amount,
          cadence: catalog.cadence,
          compareAt: unlimitedSavings
            ? completeModuleAmount(rateCatalog, catalog.cadence)
            : undefined,
          savingsPct: unlimitedSavings?.pct,
        });
      }
    }

    const isTrialing =
      entity.stripePriceId !== undefined &&
      trialingPriceIds.has(entity.stripePriceId);

    const disabledReason = optionDisabledReason({
      isActive,
      isMandatory,
      isTrialing,
      price,
    });

    return {
      ...projectEntity(entity, price),
      ...(isSmartPricing ? { name: "Smart Pricing" } : {}),
      ...(disabledReason ? { disabledReason } : {}),
      ...(isTrialing && entity.trialEndAt !== undefined
        ? { trialEndAt: entity.trialEndAt }
        : {}),
      isDisabled: disabledReason !== undefined,
      isSelected,
      isTrialing,
    };
  });
};

const projectSmartPricingVariants = ({
  catalog,
  rateCatalog,
  selection,
}: {
  catalog: BillingCatalog;
  rateCatalog: BillingCatalog;
  selection: SelectionState;
}): SmartPricingVariantViewModel[] =>
  catalog.smartPricingVariants.map((variant) => {
    const suppliedAmount =
      catalog.cadence === "year" ? variant.intervalPrice : variant.price;
    const rateVariant = rateCatalog.smartPricingVariants.find(
      ({ id }) => id === variant.id
    );
    const rateAmount =
      catalog.cadence === "year"
        ? rateVariant?.intervalPrice
        : rateVariant?.price;
    const price =
      suppliedAmount !== undefined &&
      suppliedAmount > 0 &&
      rateAmount !== undefined &&
      rateAmount > 0
        ? makePrice({ amount: rateAmount, cadence: catalog.cadence })
        : null;

    return {
      description: variant.description,
      ...(price === null ? { disabledReason: "Price unavailable" } : {}),
      id: variant.id,
      isDisabled: price === null,
      isSelected: selection.moduleIds.includes(variant.id),
      name: variant.name,
      price,
      requiresConsultation: variant.id === SMART_PRICING_UNLIMITED_ID,
    };
  });

const selectionHasPrices = (
  selection: Selection,
  catalog: BillingCatalog
): boolean => {
  const planHasPrice = selection.planId
    ? hasPrice(
        catalog.plans.find(({ id }) => id === selection.planId),
        catalog.cadence
      )
    : true;

  return (
    planHasPrice &&
    selection.moduleIds.every((id) =>
      hasPrice(
        catalog.modules.find((module) => module.id === id),
        catalog.cadence
      )
    )
  );
};

const projectBillingAdjustment = ({
  cadence,
  billingAdjustment,
  total,
}: {
  cadence: BillingCycle;
  billingAdjustment: ReturnType<typeof quoteSelection>["billingAdjustment"];
  total: BillingPriceViewModel;
}): BillingAdjustmentViewModel | null => {
  if (!billingAdjustment) {
    return null;
  }

  if (billingAdjustment.kind === "billing-cycle-reset") {
    return {
      kind: "billing-cycle-reset",
      annualCharge: makePrice({
        amount: billingAdjustment.fullNewIntervalCharge,
        cadence: "year",
      }),
      unusedMonthlyCredit: makePrice({
        amount: billingAdjustment.unusedCurrentCycleCredit,
        cadence: "month",
      }),
      dueToday: makePrice({
        amount: billingAdjustment.netDueToday,
        cadence: "year",
      }),
      asOf: billingAdjustment.asOf,
      daysRemaining: billingAdjustment.daysRemaining,
      totalDays: billingAdjustment.totalDays,
    };
  }

  return {
    kind: "proration",
    amount: makePrice({
      amount: Math.abs(billingAdjustment.netAmount),
      cadence,
    }),
    appliedAt: billingAdjustment.appliedAt,
    asOf: billingAdjustment.asOf,
    chargedToday: billingAdjustment.chargedToday,
    daysRemaining: billingAdjustment.daysRemaining,
    direction: billingAdjustment.netAmount < 0 ? "credit" : "charge",
    nextCycle: total,
    title:
      billingAdjustment.appliedAt === "next_invoice"
        ? "Prorated adjustment on your next invoice"
        : billingAdjustment.netAmount < 0
          ? "Prorated credit today"
          : "Prorated charge today",
    totalDays: billingAdjustment.totalDays,
  };
};

// Read off the active modules, matched to their trial by product, so the
// notice holds whichever cadence the merchant is choosing Unlimited at.
const unlimitedEndsModuleTrialsNotice = (
  catalog: BillingCatalog,
  selection: SelectionState
): string | null => {
  if (selection.planId !== UNLIMITED_ID) {
    return null;
  }
  const trialingModuleNames = collapseSmartPricingVariants(
    catalog.activeModules.filter(({ trialEndAt }) => trialEndAt !== undefined)
  ).map(({ id, name }) =>
    SMART_PRICING_VARIANTS.has(id) ? "Smart Pricing" : name
  );
  if (trialingModuleNames.length === 0) {
    return null;
  }
  const names = formatModuleList(trialingModuleNames);
  return trialingModuleNames.length === 1
    ? `Your ${names} trial ends when Unlimited starts. Unlimited includes it.`
    : `Your ${names} trials end when Unlimited starts. Unlimited includes them.`;
};

export const projectPlanChangeDialog = ({
  snapshot,
  catalog,
  selection,
  ordersPerMonth,
  nowSec,
  stagedCoupon,
}: {
  snapshot: BillingSnapshot;
  catalog: BillingCatalog;
  selection: SelectionState;
  ordersPerMonth: number;
  nowSec: number;
  // Pre-trial coupon staged ahead of a "create" submit — no subscription
  // exists yet to carry an operatorCoupon, so this is the only source for it.
  stagedCoupon?: Readonly<{
    id: string;
    name?: string;
    percentOff?: number;
    amountOff?: number;
  }> | null;
}): BillingPlanChangeDialogViewModel => {
  if (catalog.cadence !== selection.cadence) {
    throw new Error("catalog cadence must match selection cadence");
  }

  const rateCatalog = rateCatalogFor(snapshot, selection.cadence);
  const quote = quoteSelection(snapshot, selection, nowSec);
  const unlimited = rateCatalog.plans.find(({ id }) => id === UNLIMITED_ID);
  const selectedUnlimited =
    selection.planId === UNLIMITED_ID ? unlimited : undefined;
  // The Unlimited à-la-carte savings pct is cadence-invariant (every price
  // scales by the same annual discount), so annual carts compound the
  // marketing annual discount on top — mirroring module carts below.
  const selectedUnlimitedSavingsPct = selectedUnlimited
    ? unlimitedSavingsFor(rateCatalog, selectedUnlimited)?.pct
    : undefined;
  const compositeUnlimitedSavingsPct =
    selectedUnlimitedSavingsPct === undefined
      ? undefined
      : getEffectiveSavingsPct({
          bundleDiscountPercentage: selectedUnlimitedSavingsPct,
          annualDiscountPercentage:
            selection.cadence === "year"
              ? STATIC_MARKETING_ANNUAL_DISCOUNT_PERCENTAGE
              : 0,
        });
  // The total tells the composite savings story: the compare-at is what the
  // same selection costs with no bundle discount and — on annual carts —
  // billed at monthly rates, so the chip shows the effective (bundle +
  // annual) savings rather than the bundle discount alone.
  const undiscountedSelectionSum = getSelectionTotals(
    { plans: rateCatalog.plans, modules: rateCatalog.modules },
    selectedIdsFor(selection),
    selection.cadence
  ).selectionSum;
  const totalCompareAt =
    selection.cadence === "year"
      ? undiscountedSelectionSum /
        (1 - STATIC_MARKETING_ANNUAL_DISCOUNT_PERCENTAGE / 100)
      : undiscountedSelectionSum;
  const totalAmount =
    selection.cadence === "year" ? quote.intervalTotal : quote.monthlyTotal;

  // The staged coupon is appended, not substituted: it is about to join the
  // live stack (or start it), not replace whatever's already there.
  const operatorCoupons = [
    ...operatorCouponsFor(snapshot),
    ...(stagedCoupon ? [stagedCoupon] : []),
  ];
  const preCouponCompareAt =
    operatorCoupons.length > 0 ? totalAmount : undefined;
  const total = selectedUnlimited
    ? makePrice({
        amount: applyCouponsToAmount(totalAmount, operatorCoupons),
        cadence: selection.cadence,
        // Annual Unlimited strikes the complete à-la-carte set re-inflated to
        // monthly billing — the same fiction module carts use — so the struck
        // price and the compounded chip tell one story. Monthly Unlimited
        // keeps its bare price; the option card carries the à-la-carte
        // comparison.
        compareAt:
          selection.cadence === "year" &&
          compositeUnlimitedSavingsPct !== undefined &&
          compositeUnlimitedSavingsPct > 0
            ? completeModuleAmount(rateCatalog, selection.cadence) /
              (1 - STATIC_MARKETING_ANNUAL_DISCOUNT_PERCENTAGE / 100)
            : preCouponCompareAt,
        savingsPct: compositeUnlimitedSavingsPct,
      })
    : makePrice({
        amount: applyCouponsToAmount(totalAmount, operatorCoupons),
        cadence: selection.cadence,
        compareAt:
          quote.effectiveSavingsPct > 0 && totalCompareAt > 0
            ? totalCompareAt
            : preCouponCompareAt,
        savingsPct:
          quote.effectiveSavingsPct > 0 ? quote.effectiveSavingsPct : undefined,
      });
  // The bundle/annual/coupon story combined into one accurate percentage:
  // totalAmount is already post-bundle/annual, pre-coupon, so reversing the
  // bundle/annual compounding recovers the fully-undiscounted reference price
  // even for monthly Unlimited, which has no `total.compareAt` to derive a
  // ratio from.
  const bundleOrUnlimitedPct = selectedUnlimited
    ? (selectedUnlimitedSavingsPct ?? 0)
    : quote.bundleDiscountPct;
  const annualPct =
    selection.cadence === "year"
      ? STATIC_MARKETING_ANNUAL_DISCOUNT_PERCENTAGE
      : 0;
  const undiscountedReference =
    totalAmount / ((1 - bundleOrUnlimitedPct / 100) * (1 - annualPct / 100));
  const totalSavingsPct =
    totalAmount > 0
      ? Math.round((1 - total.amount / undiscountedReference) * 100)
      : 0;
  const bundleSavings = roundToCents(
    (undiscountedReference * bundleOrUnlimitedPct) / 100
  );
  const annualSavings = roundToCents(
    ((undiscountedReference - bundleSavings) * annualPct) / 100
  );
  // One line per stacked coupon, named individually — a generic "Coupon"
  // label would be ambiguous once more than one can be live at once.
  const couponLines: BillingSavingsLineViewModel[] = [];
  let amountBeforeCoupon = totalAmount;
  for (const coupon of operatorCoupons) {
    const amountAfterCoupon = applyCouponsToAmount(amountBeforeCoupon, [
      coupon,
    ]);
    couponLines.push({
      label: coupon.name ?? coupon.id ?? "Coupon",
      ...(coupon.percentOff != null ? { pct: coupon.percentOff } : {}),
      amount: roundToCents(amountBeforeCoupon - amountAfterCoupon),
    });
    amountBeforeCoupon = amountAfterCoupon;
  }
  const savingsBreakdown: BillingSavingsLineViewModel[] = [
    ...(bundleOrUnlimitedPct > 0
      ? [
          {
            label: "Bundle discount",
            pct: bundleOrUnlimitedPct,
            amount: bundleSavings,
          },
        ]
      : []),
    ...(annualPct > 0
      ? [{ label: "Annual billing", pct: annualPct, amount: annualSavings }]
      : []),
    ...couponLines,
  ];
  const currentSelection = getCommittedSelection(snapshot);
  const accountState = projectBillingAccountState(snapshot);
  const hasSameEntities = isSelectionNoOp(selection, currentSelection);
  const isSelectionUnchanged =
    hasSameEntities && selection.cadence === currentSelection.cadence;
  const isBridgeTrial = isBridgeOnlyTrial(snapshot);
  const isNoOp = isBridgeTrial
    ? isPlanCommitmentSelection(snapshot, selection)
    : accountState !== "trialing" && hasSameEntities;
  const trialingPriceIds = new Set(
    snapshot.subscriptionAccessData.moduleTrials.map(
      (trial) => trial.stripePriceId
    )
  );
  const hasSelection =
    selection.planId !== undefined || selection.moduleIds.length > 0;
  const selectedModules = rateCatalog.modules.filter(({ id }) =>
    selection.moduleIds.includes(id)
  );
  const options = projectDialogOptions({
    bundleSavingsPct: quote.bundleDiscountPct,
    catalog,
    rateCatalog,
    selection,
    trialingPriceIds,
  });
  const selectedTrialOption = options.find(
    ({ isSelected, isTrialing }) => isSelected && isTrialing
  );
  const trialEndsAt = selectedTrialOption?.trialEndAt;
  const totalFromTrialEnd =
    selectedTrialOption && trialEndsAt !== undefined
      ? { amount: total.amount, label: `From ${formatMonthDay(trialEndsAt)}` }
      : undefined;
  const trialingModuleIds = new Set(
    catalog.activeModules
      .filter(({ trialEndAt }) => trialEndAt !== undefined)
      .map(({ id }) => id)
  );
  const todayQuote = quoteSelection(
    snapshot,
    {
      ...selection,
      moduleIds: selection.moduleIds.filter((id) => !trialingModuleIds.has(id)),
    },
    nowSec
  );
  const totalTodayAmount = totalFromTrialEnd
    ? applyCouponsToAmount(
        selection.cadence === "year"
          ? todayQuote.intervalTotal
          : todayQuote.monthlyTotal,
        operatorCoupons
      )
    : undefined;

  return {
    bundleSavingsPct: quote.bundleDiscountPct,
    canSubmit:
      hasSelection && selectionHasPrices(selection, catalog) && !isNoOp,
    hasActiveTrialSubscription: accountState === "trialing",
    trialPlanStartsOn:
      accountState === "trialing" ? trialPlanStartsOn(snapshot) : null,
    trialEndingDisclosure:
      accountState === "trialing"
        ? trialEndingDisclosure({
            amount: total.amount,
            catalog,
            selection,
            snapshot,
          })
        : null,
    trialSubmitGoesToShopify: isBridgeTrial
      ? trialSubmitNeedsShopify(snapshot)
      : null,
    hasExpertSupport: hasExpertSupport(orderVolumeTier(ordersPerMonth)),
    hasSubscriptionToApplyCouponTo:
      snapshot.subscriptionAccessData.subscriptions.active.length > 0,
    isExistingCustomer: isExistingCustomer(
      snapshot.subscriptionAccessData.subscriptions
    ),
    isNoOp,
    isSelectionUnchanged,
    isUnlimitedEligible:
      unlimited !== undefined &&
      totalAmount >= getEntityCadencePrice(unlimited, selection.cadence),
    options,
    priceDebug: {
      annualSavingsPct:
        selection.cadence === "year"
          ? STATIC_MARKETING_ANNUAL_DISCOUNT_PERCENTAGE
          : 0,
      bundleDiscountPct: quote.calculation.bundleDiscountPct,
      discountedBeforeRounding: quote.calculation.discountedBeforeRounding,
      ...(total.compareAt !== undefined
        ? { displayCompareAt: total.compareAt }
        : {}),
      finalAmount: quote.calculation.finalAmount,
      lines: quote.calculation.lines,
      suffix: total.suffix,
      undiscountedTotal: quote.calculation.undiscountedTotal,
    },
    billingAdjustment: projectBillingAdjustment({
      cadence: selection.cadence,
      billingAdjustment: quote.billingAdjustment,
      total,
    }),
    progress: projectProgress({
      bundleSavingsPct: quote.bundleDiscountPct,
      isUnlimited: selection.planId === UNLIMITED_ID,
      modules: selectedModules,
      // The progress chip stays on the cadence-invariant à-la-carte pct;
      // only the total's chip compounds the annual discount.
      unlimitedSavingsPct: selectedUnlimitedSavingsPct,
    }),
    savingsBreakdown,
    selection: {
      cadence: toPublicCadence(selection.cadence),
      reactivatingEntityId: selection.reactivatingEntityId,
      selectedModuleIds: [...selection.moduleIds],
      selectedPlanId: selection.planId ?? null,
    },
    showContactUsLink: ordersPerMonth >= 2_000,
    smartPricingVariants: projectSmartPricingVariants({
      catalog,
      rateCatalog,
      selection,
    }),
    total,
    ...(totalFromTrialEnd ? { totalFromTrialEnd } : {}),
    ...(totalTodayAmount !== undefined ? { totalTodayAmount } : {}),
    totalSavingsPct,
    type: "plan-change",
    unlimitedEndsModuleTrialsNotice: unlimitedEndsModuleTrialsNotice(
      catalog,
      selection
    ),
  };
};
